import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getGetJournalQueryKey } from "@workspace/api-client-react";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { useToast } from "@/hooks/use-toast";
import type { MomentumItem } from "@/lib/momentum";

export const MOMENTUM_QUERY_KEY = ["/api/journals/momentum"] as const;

/** Every Next Step across the user's Unfoldings, from `GET /journals/momentum`. */
export function useMomentum() {
  return useQuery({
    queryKey: MOMENTUM_QUERY_KEY,
    queryFn: async (): Promise<MomentumItem[]> => {
      const res = await authFetch(apiUrl("/journals/momentum"));
      if (!res.ok) throw new Error(`GET /journals/momentum ${res.status}`);
      const data = (await res.json()) as { items?: MomentumItem[] };
      return Array.isArray(data.items) ? data.items : [];
    },
  });
}

/**
 * Complete / reopen a step through the same endpoint the journal's Next Steps card uses,
 * so it's one action everywhere. Optimistic, rolled back on failure; afterwards the
 * source journal's cached copy is invalidated so the journal shows the new state.
 */
export function useSetMomentumCompleted() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ item, completed }: { item: MomentumItem; completed: boolean }) => {
      const res = await authFetch(apiUrl(`/journals/${item.journalId}/actions/${item.id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed }),
      });
      if (!res.ok) throw new Error(`PATCH action ${res.status}`);
    },
    onMutate: async ({ item, completed }) => {
      await queryClient.cancelQueries({ queryKey: MOMENTUM_QUERY_KEY });
      const previous = queryClient.getQueryData<MomentumItem[]>(MOMENTUM_QUERY_KEY);
      queryClient.setQueryData<MomentumItem[]>(MOMENTUM_QUERY_KEY, (items) =>
        items?.map((i) => (i.id === item.id ? { ...i, completed } : i)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(MOMENTUM_QUERY_KEY, context.previous);
      toast({ title: "Couldn't save that", description: "Your step is unchanged — please try again." });
    },
    onSettled: (_data, _err, { item }) => {
      queryClient.invalidateQueries({ queryKey: MOMENTUM_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getGetJournalQueryKey(item.journalId) });
    },
  });
}
